import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { MessageService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { environment } from '../../../environments/environment';
import { ToggleButtonModule } from 'primeng/togglebutton';

@Component({
  selector: 'app-dmp',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    TooltipModule,
    ToggleButtonModule
  ],
  template: `
<div class="border">
  <div class="border bg-body-tertiary ">
    <div class="p-2 px-4 border-bottom">
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2">
        <div>
          <div class="d-flex align-items-center gap-1 mb-1">
            <span class="badge bg-primary rounded-pill" style="width:6px;height:14px;padding:0;"></span>
            <h3 class="mb-0 fw-semibold text-primary">
              <i class="pi pi-file-pdf me-1"></i> Upload Additional Documents by DOI
            </h3>
          </div>

          <span *ngIf="isEdit" class="fw-semibold mb-1 small">Select Number of Documents (Optional)</span>

          <div *ngIf="isEdit" class="d-inline-flex align-items-center border rounded-pill px-2 py-0 bg-light">
            <button type="button" class="btn btn-outline-secondary btn-sm py-0"
              (click)="decrementCount()" [disabled]="loading || fileCount<=0">–</button>

            <span class="px-2 fw-semibold small">{{ fileCount }}</span>

            <button type="button" class="btn btn-outline-secondary btn-sm py-0"
              (click)="incrementCount()" [disabled]="loading">+</button>
          </div>
        </div>

        <div class="d-flex flex-column align-items-md-end gap-1">
          <div class="text-secondary small mb-0">
            Total Count: <span class="fw-semibold text-primary">{{ documentsArray.length }}</span>
          </div>
        </div>
      </div>

      <div *ngIf="loading" class="mt-2 text-gray-500">
        <i class="pi pi-spinner pi-spin"></i> Loading...
      </div>
    </div>
  </div>

  <p-table
    [value]="documentsArray.controls"
    [tableStyle]="{'min-width':'100%'}"
    [loading]="loading"
    styleClass="p-datatable-striped"
    #dt
  >
    <ng-template pTemplate="header">
      <tr>
        <th rowspan="2" class="w-10 bg-blue-100 border-b border-blue-200"> Sr No.</th>

        <ng-container *ngFor="let column of filterColoum">
          <th [pSortableColumn]="column.field" pResizableColumn>
            <div class="flex items-center justify-between">
              <span>{{ column.header }}</span>
              <i class="fa-solid fa-signal fa-rotate-270 ml-4 text-xs"></i>
            </div>
          </th>
        </ng-container>

        <th *ngIf="isEdit" rowspan="2" pFrozenColumn alignFrozen="right"
          class="px-4 py-2 bg-blue-100 text-blue-900 font-semibold border-b border-blue-200 whitespace-nowrap">
          Actions
        </th>
      </tr>

      <tr>
        <th *ngFor="let column of filterColoum">
          <input
            *ngIf="column.filterType"
            type="text"
            [(ngModel)]="column.filterValue"
            (input)="dt.filter(column.filterValue, column.field, 'contains')"
            [placeholder]="column.header"
            class="w-full px-2 py-1 border-blue-200 rounded font-medium text-gray-600 text-xs"
          />
        </th>
        <!-- <th *ngIf="isEdit" pFrozenColumn alignFrozen="right"></th> -->
      </tr>
    </ng-template>

    <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
      <tr [formGroup]="row" class="hover:bg-slate-50 border-b last:border-none">
        <td class="text-center align-middle">{{ rowIndex + 1 }}</td>

        <td>
          <input *ngIf="canEditRow(rowIndex)"
            type="text"
            pInputText
            formControlName="documentName"
            placeholder="Enter document name"
            class="block w-full px-3 py-2 border-2 border-slate-500"
          />
          <span *ngIf="!canEditRow(rowIndex)">{{ row.get('documentName')?.value }}</span>
        </td>

        <td>
          <textarea *ngIf="canEditRow(rowIndex)"
            formControlName="description"
            placeholder="Enter brief description..."
            rows="2"
            class="block w-full px-3 py-2 border-2 border-slate-500"
            ></textarea>
          <span *ngIf="!canEditRow(rowIndex)">{{ row.get('description')?.value }}</span>
        </td>

        <td>
          <input
            type="file"
            #fileInput
            (change)="onFileSelect($event, rowIndex)"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.csv,.xlsx,.xls"
            class="hidden"
            [disabled]="!canEditRow(rowIndex)"
          />
        <div class="flex items-center gap-2 ">
          <button
            type="button"
            class="inline-flex items-center px-2 py-1 border-2 border-blue-500 text-blue-700 rounded hover:bg-blue-50 transition text-xs font-medium mb-1"
            (click)="fileInput.click()"
            [disabled]="!canEditRow(rowIndex) || loading"
            *ngIf="isEdit"
          >
            <i class="pi pi-upload text-xs mr-1"></i> Upload
          </button>
          <span *ngIf="!row.get('file')?.value?.name && isString(row.get('file')?.value)" >
            <a
              [href]="baseurl + row.get('file')?.value"
              target="_blank"
              class="inline-flex items-center px-2 py-1 border border-blue-400 bg-blue-50 text-blue-700 rounded shadow hover:bg-blue-100 hover:border-blue-600 transition text-xs font-medium"
              title="View File"
            >
              <i class="pi pi-eye mr-1"></i> View File
            </a>
          </span>

        </div>

          <span *ngIf="row.get('file')?.value?.name" class="block mt-1 text-gray-500 text-xs">
            <i class="pi pi-file"></i> {{ row.get('file')?.value?.name }}
          </span>

          
        </td>

        <td>
          <input
            type="date"
            pInputText
            formControlName="date"
            class="block w-full px-2 py-1 border-2 rounded border-slate-500"
            [disabled]="!canEditRow(rowIndex)"
          />
        </td>

        <td *ngIf="isEdit">
          <p-toggleButton
            formControlName="isConfidential"
            [disabled]="!canEditRow(rowIndex)"
            [onLabel]="row.get('isConfidential')?.value ? 'Confidential' : 'Not Confidential'"
            [offLabel]="row.get('isConfidential')?.value ? 'Confidential' : 'Not Confidential'"
            [onIcon]="row.get('isConfidential')?.value ? 'pi pi-lock text-red-500' : 'pi pi-unlock text-green-500'"
            [offIcon]="row.get('isConfidential')?.value ? 'pi pi-lock text-red-500' : 'pi pi-unlock text-green-500'"
            [styleClass]="
              'w-full text-xs text-white ' +
              (row.get('isConfidential')?.value
                ? 'bg-red-500 border-red-600'
                : 'bg-green-500 border-green-600')
            "
          >
          </p-toggleButton>
        </td>

        <td *ngIf="isEdit" pFrozenColumn alignFrozen="right" class="whitespace-nowrap flex gap-2">
          <button
            *ngIf="isEdit"
            type="button"
            class="border border-blue-500 text-blue-700 rounded p-1.5 bg-white hover:bg-blue-50 transition disabled:opacity-50 flex items-center justify-center"
            (click)="editRow(rowIndex)"
            [disabled]="loading"
            [ngClass]="{'bg-green-600 text-white border-green-600': editModeIndex === rowIndex, 'bg-white text-blue-700 border-blue-500': editModeIndex !== rowIndex}"
            title="{{ editModeIndex === rowIndex ? 'Save' : 'Edit' }}"
          >
            <i class="pi pi-save"></i>
          </button>

          <button
            *ngIf="isEdit"
            type="button"
            class="border border-red-500 text-red-600 rounded p-1.5 bg-white hover:bg-red-50 transition disabled:opacity-50 flex items-center justify-center"
            (click)="deleteRow(rowIndex)"
            [disabled]="loading"
            title="Delete"
          >
            <i class="pi pi-trash"></i>
          </button>
        </td>
      </tr>
    </ng-template>

    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="8" class="text-center text-gray-400 py-6 text-base">
          No documents added.
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>
`
})
export class DmpComponent implements OnInit, OnChanges, OnDestroy {
  @Input() proposalId?: string | number;
  @Input() proposalStep?: any;
  @Input() isEdit = true;
  @Input() url = '/master/proposal/dms/files/';

  documentForm: FormGroup;
  editModeIndex: number | null = null;
  loading = false;

  baseurl = environment.APIUrl;
  fileCount = 0;

  private destroy$ = new Subject<void>();

  // Note: p-table filtering resolves field paths, so we target the FormGroup
  // value object (e.g. 'value.documentName') instead of control names.
  filterColoum: any[] = [
    { field: 'value.documentName', header: 'Document Name', filterType: true, filterValue: '' },
    { field: 'value.description', header: 'Brief Description', filterType: true, filterValue: '' },
    { field: 'value.file', header: 'Document Upload', filterType: false, filterValue: '' },
    { field: 'value.date', header: 'Date', filterType: false, filterValue: '' },
    { field: 'value.isConfidential', header: 'Confidential', filterType: false, filterValue: '' }
  ];

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private message: MessageService
  ) {
    this.documentForm = this.fb.group({ documents: this.fb.array([]) });
  }

  ngOnInit(): void {
    if(!this.isEdit)
      this.filterColoum = this.filterColoum.filter(col => col.field !== 'value.isConfidential');
    
    if (this.proposalId) this.fetchDocuments();
    else this.syncCountWithArray();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['proposalId'] && !changes['proposalId'].firstChange && this.proposalId) {
      this.fetchDocuments();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get documentsArray(): FormArray {
    return this.documentForm.get('documents') as FormArray;
  }

  // ---------- UI helpers ----------
  isString(v: any): boolean { return typeof v === 'string' && !!v; }

  canEditRow(i: number): boolean {
    // Editable only if isEdit AND (new row OR currently in editModeIndex)
    const hasId = !!this.documentsArray.at(i)?.get('id')?.value;
    if (!this.isEdit) return false;
    if (!hasId) return true;               // new row
    return this.editModeIndex === i;       // existing row only when edit active
  }

  incrementCount(): void {
    this.fileCount++;
    this.updateDocuments();
  }

  decrementCount(): void {
    if (this.fileCount <= 0) return;
    this.fileCount--;
    this.updateDocuments();
  }

  // ---------- Data ----------
  fetchDocuments(): void {
    if (!this.proposalId || !this.url) return;

    this.loading = true;

    let apiUrl = `${this.url}?proposal_id=${this.proposalId}`;
    if (this.proposalStep?.proposal) apiUrl += `&proposal_step__proposal=${this.proposalStep.proposal}`;

    this.api.getAPI(apiUrl)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading = false)
      )
      .subscribe({
        next: (res: any) => this.populateDocuments(res?.data || res || []),
        error: () => this.message.add({ severity: 'error', summary: 'Error', detail: 'Failed to fetch documents' })
      });
  }

  populateDocuments(docs: any[]): void {
    this.documentsArray.clear();

    docs.forEach((doc: any) => {
      this.documentsArray.push(this.fb.group({
        id: [doc.id || null],
        documentName: [doc.name || doc.document_name || ''],
        description: [doc.description || doc.brief_description || ''],
        file: [doc.file || doc.file_url || null],
        date: [(doc.date || new Date().toISOString()).substring(0, 10)],
        isConfidential: [!!(doc.is_confidential || doc.isConfidential)]
      }));
    });

    this.editModeIndex = null;
    this.syncCountWithArray();
  }

  syncCountWithArray(): void {
    this.fileCount = this.documentsArray.length || 0;
    if (!this.fileCount && this.isEdit) {
      this.fileCount = 1;
      this.updateDocuments();
    }
  }

  updateDocuments(): void {
    const arr = this.documentsArray;

    while (arr.length < this.fileCount) {
      arr.push(this.fb.group({
        id: [null],
        documentName: [''],
        description: [''],
        file: [null],
        date: [new Date().toISOString().substring(0, 10)],
        isConfidential: [false]
      }));
    }

    while (arr.length > this.fileCount) {
      // if currently editing last row, reset editModeIndex
      if (this.editModeIndex === arr.length - 1) this.editModeIndex = null;
      arr.removeAt(arr.length - 1);
    }
  }

  // ---------- Row actions ----------
  editRow(index: number): void {
    // toggle: if same row is open => save, else open edit
    if (this.editModeIndex === index) this.saveDocument(index);
    else this.editModeIndex = index;
  }

  saveDocument(index: number): void {
    const row = this.documentsArray.at(index) as FormGroup;
    const v = row.value;

    if (!v.documentName && !v.file) {
      this.message.add({ severity: 'warn', summary: 'Validation', detail: 'Document name or file is required.' });
      return;
    }

    const fd = new FormData();
    fd.append('name', v.documentName || '');
    fd.append('description', v.description || '');
    fd.append('is_confidential', v.isConfidential);
    if (this.proposalId) fd.append('proposal', String(this.proposalId));
    if (this.proposalStep?.id) fd.append('proposal_step', String(this.proposalStep.id));
    if (v.file instanceof File) fd.append('file', v.file);

    // You are using POST with action create/update/delete
    if (v.id) {
      fd.append('id', String(v.id));
      fd.append('action', 'update');
    } else {
      fd.append('action', 'create');
    }

    this.loading = true;
    this.api.postAPI(this.url, fd)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: () => {
          this.message.add({ severity: 'success', summary: 'Success', detail: v.id ? 'Updated' : 'Created' });
          this.editModeIndex = null;
          this.fetchDocuments();
        },
        error: () => this.message.add({ severity: 'error', summary: 'Error', detail: 'Save failed' })
      });
  }

  deleteRow(index: number): void {
    const row = this.documentsArray.at(index) as FormGroup;
    const id = row.get('id')?.value;

    if (!confirm('Are you sure you want to delete this row?')) return;

    // If unsaved row, just remove
    if (!id) {
      this.documentsArray.removeAt(index);
      this.syncCountWithArray();
      return;
    }

    const fd = new FormData();
    fd.append('id', String(id));
    fd.append('action', 'delete');

    this.loading = true;
    this.api.postAPI(this.url, fd)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: () => {
          this.message.add({ severity: 'success', summary: 'Deleted', detail: 'Document removed' });
          this.editModeIndex = null;
          this.fetchDocuments();
        },
        error: () => this.message.add({ severity: 'error', summary: 'Error', detail: 'Delete failed' })
      });
  }

  onFileSelect(event: any, index: number): void {
    const file = event?.target?.files?.[0];
    if (!file) return;
    (this.documentsArray.at(index) as FormGroup).patchValue({ file });
  }
}
